import { useDisclosure } from '@chakra-ui/react';
import {
  ApprovalStatusEnum,
  IApprovalRespSchema,
  IQuoteRespSchema,
  ITeamResponseSchema,
  IUsersInfo,
  QuoteStatusEnum,
} from '@monetize/types/app';
import isNil from 'lodash/isNil';
import React, { FunctionComponent as FC, useEffect, useState } from 'react';
import { LuCircleDashed } from 'react-icons/lu';
import {
  MdCheck,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdOutlineMarkEmailRead,
} from 'react-icons/md';
import { useGetTeamsById } from '../../../api/settingsService';
import { useGetTenantUserById } from '../../../api/usersService';
import { useAuth } from '../../../services/auth0';
import { pluralize } from '../../../utils';
import { toDateTimeShort } from '../../../utils/dates';
import { sortTeamUsers } from '../../../utils/settings';
import {
  MAvatar,
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MIcon,
  MLink,
  MSpinner,
  MStack,
  MText,
  MTooltip,
  MVStack,
} from '../../Monetize';
import { DateDisplay } from '../../Monetize/DateDisplay';
import { QuoteApprovalRejectForm } from './QuoteApprovalRejectForm';

interface QuoteStepApprovalItemProps {
  quote: IQuoteRespSchema;
  approval: IApprovalRespSchema;
  canDoActions: boolean;
  isPreviousApproverApproved: boolean;
  onClose: () => void;
  onApprove: (approval: IApprovalRespSchema) => void;
  reloadData?: () => void;
  isLoading?: boolean;
}

const CAN_APPROVE_STATUS = new Set([
  ApprovalStatusEnum.NO_ACTION,
  ApprovalStatusEnum.DECLINED,
]);
const CAN_REJECT_STATUS = new Set([ApprovalStatusEnum.NO_ACTION]);

const ApprovalTeam = ({
  teamUsers,
  teamName,
  isLoading,
  statusIcon,
}: {
  teamUsers: IUsersInfo[];
  teamName: string;
  isLoading?: boolean;
  statusIcon?: React.ReactNode;
}) => {
  const [showAll, setShowAll] = useState(false);
  if (teamUsers.length === 0) {
    return null;
  }
  if (!showAll) {
    return (
      <MFlex flexWrap="wrap" alignItems="center">
        {statusIcon}
        <MText
          fontSize="xs"
          truncate
          color="tGray.darkPurple"
          onClick={() => setShowAll(true)}
          cursor="pointer"
        >
          {teamName} ({teamUsers?.length}{' '}
          {pluralize('Reviewer', teamUsers?.length)})
        </MText>
        <MCustomIconButton
          ml="1"
          btnSize={5}
          variant="icon"
          icon={MdChevronRight}
          aria-label="Show reviewers"
          iconColor="tGray.darkPurple"
          onClick={() => setShowAll(true)}
        />
      </MFlex>
    );
  }
  if (showAll) {
    return (
      <MBox>
        <MFlex alignItems="center" ml="-1">
          <MCustomIconButton
            ml="1"
            btnSize={5}
            variant="icon"
            icon={MdChevronLeft}
            aria-label="Hide reviewers"
            iconColor="tGray.darkPurple"
            onClick={() => setShowAll(false)}
          />
          <MText
            fontSize="xs"
            truncate
            ml="1"
            color="tGray.darkPurple"
            onClick={() => setShowAll(false)}
            cursor="pointer"
          >
            {teamName} ({teamUsers?.length}{' '}
            {pluralize('Reviewer', teamUsers?.length)})
          </MText>
        </MFlex>
        <MVStack alignItems="flex-start" gap="1.5" my="2" ml="5">
          {teamUsers.map((member: IUsersInfo) => (
            <MFlex key={member.id} alignItems="center" maxW="100%">
              <MAvatar name={member.name} size="3xs" mr="2" />
              <MText truncate fontSize="xs" color="tGray.darkPurple">
                {member.name}
              </MText>
            </MFlex>
          ))}
        </MVStack>
      </MBox>
    );
  }
  return null;
};

export const QuoteStepApprovalItem: FC<QuoteStepApprovalItemProps> =
  React.forwardRef<any, QuoteStepApprovalItemProps>(
    (
      {
        quote,
        approval,
        canDoActions,
        isPreviousApproverApproved,
        onClose,
        onApprove,
        reloadData,
        isLoading,
      }: QuoteStepApprovalItemProps,
      ref,
    ) => {
      const { tenantId } = useAuth();
      const { teamId, userId } = approval;
      const [showMore, setShowMore] = useState(false);

      const [pendingAction, setPendingAction] = useState<
        'approve' | 'deny' | null
      >(null);

      useEffect(() => {
        if (!isLoading) {
          setPendingAction(null);
        }
      }, [isLoading]);

      const { data: teamUsers, isLoading: teamLoading } = useGetTeamsById(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        teamId!,
        {
          enabled: !!teamId,
          select: (team: ITeamResponseSchema) => {
            return sortTeamUsers(team?.users, team?.owner);
          },
        },
      );

      const { data: user, isLoading: userLoading } = useGetTenantUserById({
        tenantId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        userId: userId!,
        options: {
          enabled: !!userId,
          refetchOnWindowFocus: false,
        },
      });

      const {
        open: isRejectFormOpen,
        onOpen: onOpenRejectForm,
        onClose: onCloseRejectForm,
      } = useDisclosure();

      const canApprove =
        isPreviousApproverApproved &&
        canDoActions &&
        quote.status !== QuoteStatusEnum.DENIED &&
        CAN_APPROVE_STATUS.has(approval.status as ApprovalStatusEnum);
      const canReject =
        isPreviousApproverApproved &&
        canDoActions &&
        quote.status !== QuoteStatusEnum.DENIED &&
        CAN_REJECT_STATUS.has(approval.status as ApprovalStatusEnum);

      const isApproved =
        approval.status === ApprovalStatusEnum.APPROVED || approval.skipped;
      const isDeclined = approval.status === ApprovalStatusEnum.DECLINED;
      const approverName =
        approval.approverName ??
        (approval.teamId ? approval.teamName : approval.username);

      const REASON_SLICE_LENGTH = 100;
      const showShowMore =
        isDeclined &&
        approval.declineReason &&
        approval.declineReason.length > REASON_SLICE_LENGTH;

      const statusIcon = (() => {
        if (!isNil(approval.notifiedAt)) {
          return (
            <MTooltip
              shouldWrapChildren
              label={`Notified at ${toDateTimeShort(approval.notifiedAt)}`}
            >
              <MIcon
                as={MdOutlineMarkEmailRead}
                mr={1}
                color="tGray.darkPurple"
                verticalAlign="middle"
              />
            </MTooltip>
          );
        }
        return <MIcon as={LuCircleDashed} mr={1} color="tBlue.base" />;
      })();

      return (
        <MBox w="100%" mb={4}>
          {isApproved && (
            <MFlex>
              <MIcon as={MdCheck} mr={1} color="tGreen.approval" />
              <MStack>
                <MFlex>
                  <MText
                    fontSize="xs"
                    whiteSpace="nowrap"
                    mr="1"
                    color="tGray.darkPurple"
                  >
                    Approved by
                  </MText>
                  <MTooltip label={approverName} isDisabled={isLoading}>
                    <MText fontSize="xs" truncate color="tGray.darkPurple">
                      {approverName}
                    </MText>
                  </MTooltip>
                </MFlex>

                {approval.approvalTimestamp && (
                  <DateDisplay
                    date={approval.approvalTimestamp}
                    timezoneConfigKey="userTimezone"
                    textProps={{
                      fontSize: 'xs',
                      whiteSpace: 'nowrap',
                      color: 'tGray.darkPurple',
                    }}
                  />
                )}
                {approval.teamName && (
                  <MText fontSize="xs" color="tGray.darkPurple">
                    {approval.teamName}
                  </MText>
                )}
              </MStack>
            </MFlex>
          )}
          {!isApproved && !isDeclined && (
            <>
              {!!teamId && teamLoading && <MSpinner />}
              {!!userId && userLoading && <MSpinner />}
              {!!teamId && !teamLoading && approval.teamName && (
                <ApprovalTeam
                  teamUsers={teamUsers as IUsersInfo[]}
                  teamName={approval.teamName}
                  isLoading={isLoading}
                  statusIcon={statusIcon}
                />
              )}
              {!!userId && !!user && !userLoading && (
                <MFlex mt={1} alignItems="center">
                  {/* this will handle backward compatible icon */}
                  {statusIcon}

                  {/* <MAvatar name={user.name} mr="1" size="2xs" /> */}
                  <MTooltip
                    shouldWrapChildren
                    label={user.name}
                    placement="bottom-start"
                    isDisabled={isLoading}
                  >
                    <MText
                      fontSize="xs"
                      truncate
                      lineHeight="18px"
                      color="tGray.darkPurple"
                    >
                      {user.name}
                    </MText>
                  </MTooltip>
                </MFlex>
              )}
            </>
          )}

          {isDeclined && (
            <MFlex>
              <MIcon as={MdClose} mr={1} color="tRed.base" />
              <MStack>
                <MFlex alignItems="center">
                  <MText
                    fontSize="xs"
                    whiteSpace="nowrap"
                    mr="1"
                    color="tGray.darkPurple"
                  >
                    Declined by
                  </MText>
                  <MTooltip
                    label={approval.declinerName}
                    isDisabled={isLoading}
                  >
                    <MText fontSize="xs" truncate color="tGray.darkPurple">
                      {approval.declinerName}
                    </MText>
                  </MTooltip>
                </MFlex>
                {approval.declinedTimestamp && (
                  <DateDisplay
                    date={approval.declinedTimestamp}
                    timezoneConfigKey="userTimezone"
                    textProps={{
                      fontSize: 'xs',
                      whiteSpace: 'nowrap',
                      color: 'tGray.darkPurple',
                    }}
                  />
                )}
                <MFlex alignItems="flex-end">
                  <MText color="tGray.darkPurple" fontSize="xs">
                    <b>Reason</b>:{' '}
                    {showShowMore && !showMore
                      ? `${(approval.declineReason || '').slice(
                          0,
                          REASON_SLICE_LENGTH,
                        )}...`
                      : approval.declineReason}
                  </MText>
                  {showShowMore && (
                    <MLink fontSize="xs" onClick={() => setShowMore(!showMore)}>
                      {!showMore ? 'More' : 'Less'}
                    </MLink>
                  )}
                </MFlex>
              </MStack>
            </MFlex>
          )}

          {canDoActions && (
            <>
              {isRejectFormOpen && (
                <QuoteApprovalRejectForm
                  isOpen={isRejectFormOpen}
                  onClose={onCloseRejectForm}
                  quote={quote}
                  approval={approval}
                  reloadData={reloadData}
                />
              )}
              {!isRejectFormOpen && (canApprove || canReject) && (
                <MFlex mt={2} justifyContent="flex-start">
                  {canReject && (
                    <MButton
                      variant={'deleteOutline'}
                      onClick={() => {
                        setPendingAction('deny');
                        onOpenRejectForm();
                      }}
                      minW="unset"
                      isLoading={isLoading && pendingAction === 'deny'}
                      isDisabled={isLoading && pendingAction === 'approve'}
                    >
                      Deny
                    </MButton>
                  )}

                  {canApprove && (
                    <MButton
                      ml={2}
                      onClick={() => {
                        setPendingAction('approve');
                        onApprove(approval);
                      }}
                      minW="unset"
                      isLoading={isLoading && pendingAction === 'approve'}
                      isDisabled={isLoading && pendingAction === 'deny'}
                    >
                      Approve
                    </MButton>
                  )}
                </MFlex>
              )}
            </>
          )}
        </MBox>
      );
    },
  );
